import { useState } from "react";
import { formatContractError } from "@/lib/error";
import { formatUnits, parseUnits, JsonRpcSigner } from "ethers";
import { useContracts } from "@/hooks/useContracts";
import { useCatDetails } from "@/hooks/useCatDetails";
import { useCatclawAllowance } from "@/hooks/useCatclawAllowance";
import { useCatclawBalance } from "@/hooks/useCatclawBalance";
import { ADDRESSES, getContracts, getProvider } from "@/lib/contracts";
import CatStonksAbi from "../../../public/abis/CatStonks.json";
import { useWalletClient } from "wagmi";
import { encodeFunctionData } from "viem";
import { Button } from "@/components/ui/button";
import { Loader2, Zap } from "lucide-react";
import { refreshAfterTx } from "@/lib/refreshAll";

const ACTIVATE_COST = 25000n;

interface ActivatePanelProps {
  tokenId?: number | null;
  onDone?: () => void;
}

export function ActivatePanel({ tokenId, onDone }: ActivatePanelProps) {
  const { account, connected, requestConnect, signer } = useContracts();
  const { data: walletClient } = useWalletClient();
  const { details } = useCatDetails(tokenId);
  const { allowance, approve, refresh: refreshAllowance } =
    useCatclawAllowance(ADDRESSES.CatStonks);
  const { balance, decimals } = useCatclawBalance(account);
  const [isPending, setIsPending] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!connected) {
    return (
      <Button variant="pixel" size="pixel" className="w-full" onClick={requestConnect}>
        Connect Wallet
      </Button>
    );
  }

  if (!tokenId) {
    return (
      <p className="text-sm text-muted-foreground">Select a cat from My Cats to activate.</p>
    );
  }

  if (details?.active) {
    return <p className="text-sm text-success">This cat is already active.</p>;
  }

  if (details && details.rarityWeight === 0n) {
    return (
      <p className="text-sm text-danger">
        This cat's rarity is not set yet. Please wait for the backend to set rarity before activating.
      </p>
    );
  }

  const required = parseUnits(ACTIVATE_COST.toString(), decimals);
  const needsApprove = allowance < required;
  const hasEnoughCatclaw = balance >= required;
  const ownerMismatch = Boolean(
    details?.owner && account && details.owner.toLowerCase() !== account.toLowerCase()
  );

  const handleActivate = async () => {
    if (!tokenId) return;
    setError(null);

    if (!account) {
      setError("Wallet not connected.");
      return;
    }

    if (!hasEnoughCatclaw) {
      setError(
        `Insufficient $CATCLAW balance. Need ${formatUnits(required, decimals)}, have ${formatUnits(balance, decimals)}.`
      );
      return;
    }

    setIsPending(true);
    try {
      // 使用项目 RPC 明确校验当前 token 主人，避免页面显示账户与真实 owner 不一致
      const { nft: readNft } = getContracts(getProvider());
      let actualOwner: string;
      try {
        actualOwner = (await readNft.ownerOf(tokenId)) as string;
      } catch (ownerErr) {
        setError(`Failed to verify cat owner: ${formatContractError(ownerErr)}`);
        setIsPending(false);
        return;
      }
      if (actualOwner.toLowerCase() !== account.toLowerCase()) {
        setError(
          `Not the token owner. This cat is owned by ${actualOwner}. Please switch to that wallet account.`
        );
        setIsPending(false);
        return;
      }

      // 校验 wagmi 账户与 ethers signer 是否同步，防止钱包切账户后 signer 仍是旧地址
      if (signer) {
        const signerAddr = await (signer as JsonRpcSigner).getAddress();
        if (signerAddr.toLowerCase() !== account.toLowerCase()) {
          setError(
            `Wallet account mismatch: page shows ${account}, but ready signer is ${signerAddr}. Please reconnect your wallet.`
          );
          setIsPending(false);
          return;
        }
      }

      // 使用项目 RPC 做静态调用，必须传入 from 以真实模拟 msg.sender
      // 注意：static call 要在 approve 之后进行，否则 allowance 不足会 revert
      if (needsApprove) {
        setStep("Approve");
        await approve();
        await refreshAllowance();
      }

      try {
        await readNft.activate.staticCall(tokenId, { from: account });
      } catch (staticErr) {
        setError(formatContractError(staticErr));
        setIsPending(false);
        return;
      }

      setStep("Activate");
      if (!walletClient) {
        setError("Wallet client not ready");
        setIsPending(false);
        return;
      }

      const data = encodeFunctionData({
        abi: CatStonksAbi,
        functionName: "activate",
        args: [BigInt(tokenId)],
      });
      const provider = getProvider();
      const feeData = await provider.getFeeData();
      const gasPrice = feeData.gasPrice ?? 1n * 10n ** 9n;
      const gasEstimate = await provider.estimateGas({
        to: ADDRESSES.CatStonks,
        from: account,
        data,
      });
      const gasLimit = (gasEstimate * 120n) / 100n;

      console.log("[ActivatePanel] sending legacy tx", {
        to: ADDRESSES.CatStonks,
        from: account,
        data,
        gasPrice: gasPrice.toString(),
        gasLimit: gasLimit.toString(),
      });

      let txHash: `0x${string}`;
      try {
        // OKX 钱包在自定义链上对 EIP-1559 支持不稳定，改用 legacy 交易
        txHash = await walletClient.sendTransaction({
          to: ADDRESSES.CatStonks as `0x${string}`,
          data,
          gas: gasLimit,
          gasPrice,
          type: "legacy",
        });
      } catch (sendErr) {
        console.error("[ActivatePanel] legacy send failed", sendErr);
        throw sendErr;
      }
      console.log("[ActivatePanel] tx hash", txHash);

      await provider.waitForTransaction(txHash, 1, 30000);
      await refreshAllowance();
      await refreshAfterTx(tokenId);
      onDone?.();
    } catch (err) {
      setError(formatContractError(err));
    } finally {
      setIsPending(false);
      setStep(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-background p-4">
          <p className="font-pixel text-xs text-primary">COST</p>
          <p className="mt-1 font-mono text-sm">{ACTIVATE_COST.toString()} $CATCLAW</p>
        </div>
        <div className="rounded-xl border border-border bg-background p-4">
          <p className="font-pixel text-xs text-primary">BALANCE</p>
          <p className={cn("mt-1 font-mono text-sm", !hasEnoughCatclaw && "text-danger")}>
            {formatUnits(balance, decimals)} $CATCLAW
          </p>
        </div>
      </div>

      {!hasEnoughCatclaw && (
        <p className="text-xs text-danger">
          Not enough $CATCLAW. You need {formatUnits(required, decimals)} to activate.
        </p>
      )}

      {ownerMismatch && (
        <p className="text-xs text-danger">
          Not the token owner. This cat is owned by {details?.owner}. Please switch to that wallet account.
        </p>
      )}

      <Button
        variant="pixel"
        size="pixel"
        className="w-full"
        onClick={handleActivate}
        disabled={isPending || !hasEnoughCatclaw || ownerMismatch}
      >
        {isPending ? (
          <span className="flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {step ?? "Activating"}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Zap size={16} />
            Activate
          </span>
        )}
      </Button>

      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}
